import React, { useEffect, useState } from 'react';
import { BookOpen } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { AccountingLayout } from '../components/AccountingLayout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { listAccounts, accountLedger } from '../lib/api';
import { formatRp } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function LedgerPage() {
  const [params] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(params.get('account_id') || '');
  const [start, setStart] = useState(() => {
    const q = params.get('start');
    if (q) return q;
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(params.get('end') || todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listAccounts({ is_active: true }).then((r) => {
      setAccounts(r);
      if (r.length && !accountId) setAccountId(r[0].id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    accountLedger(accountId, { start, end })
      .then(setData)
      .finally(() => setLoading(false));
  }, [accountId, start, end]);

  return (
    <AccountingLayout>
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
            JurnalKu · Buku Besar
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
            Buku Besar (General Ledger)
          </h1>
          <p className="text-sm text-stone-600 mt-2">
            Riwayat transaksi per akun dengan saldo berjalan.
          </p>
        </div>

        <div className="card-modern p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-[280px] flex-1">
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Akun</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="bg-white" data-testid="ledger-account-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="font-mono text-xs mr-2">{a.code}</span>{a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Dari</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="ledger-start" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Sampai</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="ledger-end" />
          </div>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="card-modern p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Akun</div>
                <div className="font-mono text-xs text-stone-500 mt-1">{data.account.code}</div>
                <div className="font-heading font-semibold text-stone-900">{data.account.name}</div>
              </div>
              <div className="card-modern p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Debit</div>
                <div className="num text-lg font-semibold text-stone-900">{formatRp(data.total_debit)}</div>
              </div>
              <div className="card-modern p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Kredit</div>
                <div className="num text-lg font-semibold text-stone-900">{formatRp(data.total_credit)}</div>
              </div>
              <div className="card-modern p-4 border-emerald-200 bg-emerald-50">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700">Saldo Akhir</div>
                <div className={`num text-lg font-semibold ${data.ending_balance < 0 ? 'text-red-700' : 'text-emerald-900'}`}>
                  {formatRp(data.ending_balance)}
                </div>
              </div>
            </div>

            <div className="card-modern overflow-hidden">
              <table className="w-full ledger" data-testid="ledger-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Tanggal</th>
                    <th style={{ width: '10%' }}>Ref</th>
                    <th>Deskripsi</th>
                    <th style={{ width: '10%' }}>Cabang</th>
                    <th style={{ width: '11%' }} className="text-right">Debit</th>
                    <th style={{ width: '11%' }} className="text-right">Kredit</th>
                    <th style={{ width: '13%' }} className="text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.rows].reverse().map((r, i) => (
                    <tr key={i}>
                      <td className="num text-xs">{r.date}</td>
                      <td className="font-mono text-xs font-semibold text-emerald-900">{r.ref}</td>
                      <td className="text-sm text-stone-700">
                        {r.description || <span className="text-stone-400">—</span>}
                        {r.memo && <div className="text-[11px] text-stone-500">{r.memo}</div>}
                      </td>
                      <td className="text-xs text-stone-600">{r.branch_name || '—'}</td>
                      <td className="num-cell">{r.debit > 0 ? formatRp(r.debit) : ''}</td>
                      <td className="num-cell">{r.credit > 0 ? formatRp(r.credit) : ''}</td>
                      <td className="num-cell font-semibold">{formatRp(r.balance)}</td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && !loading && (
                    <tr><td colSpan={7} className="text-center text-stone-500 py-8">Tidak ada transaksi.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!data && !loading && (
          <div className="card-modern p-10 text-center text-stone-500">
            <BookOpen className="w-8 h-8 mx-auto mb-2" /> Pilih akun untuk melihat buku besar.
          </div>
        )}
    </AccountingLayout>
  );
}
